import React, { useEffect, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyList from "go-core/components/EmptyList";
import { Loading } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { SubscriptionsApi } from "../../../../../../../services/Api/Subscription/subscriptionTypes";
import { OrganizationApi } from "../../../../../../../services/Api/types";
import SubscriptionTotalPrice from "../../../../../pages/Organization/pages/Subscriptions/components/Preview/SubscriptionTotalPrice";
import { OrganizationPreviewRequests } from "../../services/types";
import OrganizationSingleSubscriptionItem from "../OrganizationSingleSubscriptionItem";

interface Props {
	organization: OrganizationApi;
	requests: OrganizationPreviewRequests;
	onRefresh: () => Promise<void>;
	editable?: boolean;
}

const SubscriptionsTabPreview = ({ organization, requests, onRefresh, editable }: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState(false);
	const [subscriptionsData, setSubscriptionsData] = useState<SubscriptionsApi | undefined>(undefined);
	const subscriptions = subscriptionsData?.subscriptions ? [...subscriptionsData?.subscriptions] : [];
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();

	useEffect(() => {
		fetchSubscriptionsData();
	}, []);

	const onEndTrial = async () => {
		if (!requests.subscriptionChangeTrial) return;

		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("modules.organization.field.confirmation_end_trial.title"),
			});
			await requests.subscriptionChangeTrial(organization.id, { trial: false });
			await onRefresh();
			await fetchSubscriptionsData();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const onStartTrial = async () => {
		if (!requests.subscriptionChangeTrial) return;

		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("modules.organization.field.confirmation_start_trial.title"),
			});
			await requests.subscriptionChangeTrial(organization.id, { trial: true });
			await onRefresh();
			await fetchSubscriptionsData();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const fetchSubscriptionsData = async () => {
		setLoading(true);
		try {
			const res = await requests.subscriptions(organization.id);
			setSubscriptionsData(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const checkIfTrialIsEnabled = () => {
		const trialEnabledCf = organization?.custom_fields?.find((cf) => cf.slug === "trial_enabled");
		if (trialEnabledCf?.value === "true" || !trialEnabledCf) return true;
		return false;
	};

	return (
		<Card className="subscriptions-card">
			<Card.Body>
				{loading ? (
					<Loading />
				) : subscriptionsData && subscriptions.length > 0 ? (
					<>
						<div className="d-flex align-items-center mb-3">
							<h5>{t("common.word.subscriptions")}</h5>
							{requests.subscriptionChangeTrial !== undefined &&
								(checkIfTrialIsEnabled() ? (
									<Button variant={"default"} className="ms-auto" onClick={() => onEndTrial()}>
										{t("modules.organization.action.end_trial.title")}
									</Button>
								) : (
									<Button variant={"default"} className="ms-auto" onClick={() => onStartTrial()}>
										{t("modules.organization.action.start_trial.title")}
									</Button>
								))}
						</div>
						{subscriptionsData.customer.external_id && (
							<p className="text-muted">CustomerId: {subscriptionsData.customer.external_id}</p>
						)}
						<div className={"table-responsive"}>
							<table className={"table table-preview"}>
								<thead>
									<tr>
										<th>{t("common.word.app")}</th>
										<th>{t("modules.organization.field.additions.title")}</th>
										<th>{t("common.word.total_price")}</th>
									</tr>
								</thead>
								<tbody>
									{subscriptions.map((subscription, index) => (
										<OrganizationSingleSubscriptionItem subscription={subscription} key={index} />
									))}
								</tbody>
							</table>
						</div>
						<hr className="mt-0" style={{ marginBottom: "14px" }} />
						<h5>{t("lib:common.word.summary")}</h5>
						<div className={"subscriptions-summary"}>
							<h6>{`${t("common.word.total_price")}:`}</h6>
							<div className="d-flex flex-column align-items-end subscriptions-summary-total-price">
								<SubscriptionTotalPrice totals={subscriptionsData.totals} />
							</div>
						</div>
						{editable && (
							<Link className={"btn btn-primary"} to={`/${organization.id}/subscriptions/edit`}>
								{t("modules.organization.action.change_subscription.title")}
							</Link>
						)}
					</>
				) : (
					<EmptyList
						title={t("common.word.subscriptions")}
						description={t("modules.organization.field.no_subscriptions.description")}
						actions={[
							{
								name: checkIfTrialIsEnabled()
									? t("modules.organization.action.end_trial.title")
									: t("modules.organization.action.start_trial.title"),
								variant: "default",
								click: () => (checkIfTrialIsEnabled() ? onEndTrial() : onStartTrial()),
							},
						]}
					/>
				)}
			</Card.Body>
		</Card>
	);
};

export default SubscriptionsTabPreview;
